<script setup lang="ts">
const props = defineProps<{
  hasExperience: boolean;
  hasEducation: boolean;
  hasContacts: boolean;
}>();

const emit = defineEmits<{
  stickyChange: [value: boolean];
}>();

const sentinelRef = ref<HTMLElement | null>(null);
const scrollerRef = ref<HTMLElement | null>(null);
const itemRefs = new Map<string, HTMLButtonElement>();
const activeSection = ref("about");
const isSticky = ref(false);

let stickyObserver: IntersectionObserver | null = null;
let scrollFrame: number | null = null;

const items = computed(() => [
  { id: "about", label: "worker_profile.about" },
  ...(props.hasExperience
    ? [{ id: "experience", label: "worker_profile.experience" }]
    : []),
  ...(props.hasEducation
    ? [{ id: "education", label: "worker_profile.education" }]
    : []),
  { id: "portfolio", label: "worker_profile.services_and_portfolio" },
  ...(props.hasContacts
    ? [{ id: "contacts", label: "worker_profile.contacts" }]
    : []),
  { id: "reviews", label: "worker_profile.reviews" },
]);

function setItemRef(id: string, element: unknown) {
  if (element instanceof HTMLButtonElement) {
    itemRefs.set(id, element);
    return;
  }

  itemRefs.delete(id);
}

function revealActiveItem(id: string) {
  const scroller = scrollerRef.value;
  const item = itemRefs.get(id);

  if (!scroller || !item || scroller.scrollWidth <= scroller.clientWidth) {
    return;
  }

  const gutter = 16;
  const itemLeft = item.offsetLeft;
  const itemRight = itemLeft + item.offsetWidth;
  const visibleLeft = scroller.scrollLeft + gutter;
  const visibleRight = scroller.scrollLeft + scroller.clientWidth - gutter;
  let left = scroller.scrollLeft;

  if (itemLeft < visibleLeft) {
    left = itemLeft - gutter;
  } else if (itemRight > visibleRight) {
    left = itemRight - scroller.clientWidth + gutter;
  } else {
    return;
  }

  scroller.scrollTo({
    left: Math.max(
      0,
      Math.min(left, scroller.scrollWidth - scroller.clientWidth),
    ),
    behavior: "smooth",
  });
}

function updateActiveSection() {
  scrollFrame = null;

  const activationLine = isSticky.value ? 150 : 100;
  let current = items.value[0]?.id || "about";

  for (const item of items.value) {
    const element = document.getElementById(item.id);
    if (!element) continue;

    if (element.getBoundingClientRect().top <= activationLine) {
      current = item.id;
      continue;
    }

    break;
  }

  if (
    window.scrollY + window.innerHeight >=
    document.documentElement.scrollHeight - 4
  ) {
    current = items.value.at(-1)?.id || current;
  }

  if (activeSection.value !== current) {
    activeSection.value = current;
    revealActiveItem(current);
  }
}

function scheduleActiveSectionUpdate() {
  if (scrollFrame !== null) return;

  scrollFrame = window.requestAnimationFrame(updateActiveSection);
}

function scrollToSection(id: string) {
  activeSection.value = id;
  revealActiveItem(id);

  document.getElementById(id)?.scrollIntoView({
    behavior: "smooth",
    block: "start",
  });
}

onMounted(() => {
  if (sentinelRef.value) {
    stickyObserver = new IntersectionObserver(
      ([entry]) => {
        const next = !entry.isIntersecting;
        if (next === isSticky.value) return;

        isSticky.value = next;
        emit("stickyChange", next);

        nextTick(() => {
          revealActiveItem(activeSection.value);
          scheduleActiveSectionUpdate();
        });
      },
      { threshold: 0 },
    );

    stickyObserver.observe(sentinelRef.value);
  }

  window.addEventListener("scroll", scheduleActiveSectionUpdate, {
    passive: true,
  });
  window.addEventListener("resize", scheduleActiveSectionUpdate);

  scheduleActiveSectionUpdate();
});

onBeforeUnmount(() => {
  stickyObserver?.disconnect();
  window.removeEventListener("scroll", scheduleActiveSectionUpdate);
  window.removeEventListener("resize", scheduleActiveSectionUpdate);

  if (scrollFrame !== null) {
    window.cancelAnimationFrame(scrollFrame);
  }
});
</script>

<template>
  <div ref="sentinelRef" class="h-px" aria-hidden="true" />

  <nav
    :aria-label="$t('worker_profile.profile_navigation')"
    class="z-40 bg-white"
    :class="
      isSticky
        ? 'fixed inset-x-0 top-16 border-y border-app-border bg-white shadow-[0_6px_18px_rgba(15,23,42,0.05)]'
        : 'relative'
    "
  >
    <div :class="isSticky ? 'mx-auto max-w-[1280px] px-0 sm:px-6' : ''">
      <div
        ref="scrollerRef"
        class="profile-anchor-scroll w-full scroll-px-4 overflow-x-auto overscroll-x-contain"
      >
        <div
          class="flex min-w-max items-center"
          :class="
            isSticky
              ? 'gap-6 bg-white px-4 sm:px-0'
              : 'gap-1 rounded-xl bg-gray-100 p-1.5'
          "
        >
          <button
            v-for="item in items"
            :key="item.id"
            :ref="element => setItemRef(item.id, element)"
            type="button"
            class="relative inline-flex shrink-0 items-center whitespace-nowrap text-sm font-semibold transition"
            :class="
              isSticky
                ? activeSection === item.id
                  ? 'h-12 rounded-none px-0 text-app-text after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:rounded-full after:bg-primary'
                  : 'h-12 rounded-none px-0 text-app-muted hover:text-app-text'
                : activeSection === item.id
                  ? 'h-9 rounded-lg bg-white px-3.5 text-app-text shadow-sm ring-1 ring-black/5'
                  : 'h-9 rounded-lg px-3.5 text-app-muted hover:bg-white/70 hover:text-app-text'
            "
            @click="scrollToSection(item.id)"
          >
            {{ $t(item.label) }}
          </button>
        </div>
      </div>
    </div>
  </nav>

  <div v-if="isSticky" class="h-[49px]" aria-hidden="true" />
</template>

<style scoped>
.profile-anchor-scroll {
  scrollbar-width: none;
}

.profile-anchor-scroll::-webkit-scrollbar {
  display: none;
}
</style>
